/**
 * GenChemUpload.jsx
 *
 * Shell page for structure-based generative chemistry workflows.
 * Reads the pathname to determine which workflow is active:
 *   /…/diffhop-upload    → DiffHop scaffold hopping
 *   /…/difflinker-upload → DiffLinker fragment linking
 *
 * Owns all shared state:
 *   - PDB target (ID or file), chain selection, Molstar viewer
 *   - Submission lifecycle (loader, modal, reset)
 *
 * Delegates workflow-specific controls to:
 *   - DiffHopInputs.jsx
 *   - DiffLinkerInputs.jsx
 */

import React, {
  useRef, useState, useEffect, useCallback, useContext,
} from 'react';
import {
  Upload, Dna, Loader2, AlertCircle, CheckCircle2, X,
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';

import ChainSelector from './ChainSelector';
import DiffHopInputs from './DiffHopInputs';
import DiffLinkerInputs from './DiffLinkerInputs';
import QueueNotificationModal from '../../retro/QueueNotificationModal';
import Loader from '../../Loader';
import { UserContext } from '../../UserContext';

import {
  resolvePdbId,
  filterPdbChains,
  parsePdbChains,
  generateScaffolds,
} from './diffhopService';
import { generateLinker } from './difflinkerService';

// ─── Molstar ──────────────────────────────────────────────────────────────────
const MOLSTAR_ORIGIN = 'https://cascade-454406.web.app';
const MOLSTAR_URL    = `${MOLSTAR_ORIGIN}/viewer/index.html?v=20260424-4`;

if (typeof document !== 'undefined' && !document.getElementById('genchem-spin-kf')) {
  const st = document.createElement('style');
  st.id = 'genchem-spin-kf';
  st.textContent = `@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`;
  document.head.appendChild(st);
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const st = {
  root: {
    display: 'flex',
    gap: 16,
    padding: '0 16px 16px',
    height: 'calc(100vh - 104px)', // 64px navbar + 16px mt-4 + 24px breadcrumb row
    overflow: 'hidden',
    fontFamily: "'Inter', system-ui, sans-serif",
    boxSizing: 'border-box',
    background: '#F4F4F4',
  },
  breadcrumb: {
    fontSize: 13,
    color: '#9690b0',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: '10px 16px 0',
    gap: 2,
    flexShrink: 0,
  },
  // ── LEFT viewer card ──
  previewCard: {
    flex: '1 1 0',
    minWidth: 0,
    background: '#fff',
    borderRadius: 16,
    border: '1px solid #ede9f8',
    display: 'flex',
    flexDirection: 'column',
    padding: 16,
    gap: 10,
    overflow: 'hidden',
  },
  previewHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  previewTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#1a1a2e',
    letterSpacing: '-0.01em',
  },
  workflowPill: {
    fontSize: 11,
    fontWeight: 600,
    color: '#fff',
    borderRadius: 20,
    padding: '3px 10px',
    letterSpacing: '0.01em',
  },
  molstarBadge: {
    fontSize: 11,
    fontWeight: 500,
    color: '#7c5cbf',
    background: '#ede9f8',
    borderRadius: 8,
    padding: '3px 9px',
  },
  viewerWrap: {
    flex: 1,
    position: 'relative',
    minHeight: 0,
    borderRadius: 10,
    background: '#faf9fe',
    border: '1px solid #ede9f8',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iframeLayer: {
    position: 'absolute',
    inset: 0,
    transition: 'opacity 0.3s ease',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    padding: '0 40px',
    textAlign: 'center',
  },
  emptyText: {
    fontSize: 13,
    color: '#a89dcf',
    margin: 0,
    lineHeight: 1.6,
  },
  centeredState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    textAlign: 'center',
  },
  stateText:  { fontSize: 13, color: '#9d84d8', margin: 0 },
  errorText:  { fontSize: 13, color: '#e05252', margin: 0 },
  spinner:    { animation: 'spin 1s linear infinite' },
  banner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 11,
    lineHeight: 1.5,
    flexShrink: 0,
  },
  // ── RIGHT controls col ──
  controlsCol: {
    width: 320,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    overflowY: 'auto',
    paddingRight: 2,
  },
  card: {
    background: '#fff',
    borderRadius: 14,
    border: '1px solid #ede9f8',
    padding: '14px 14px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    flexShrink: 0,
  },
  cardHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardLabel: {
    margin: 0,
    fontSize: 13,
    fontWeight: 700,
    color: '#1a1a2e',
    letterSpacing: '-0.01em',
  },
  exampleBtn: {
    fontSize: 11,
    fontWeight: 600,
    color: '#7c5cbf',
    background: '#f0ecfa',
    border: '1px solid #d9cff5',
    borderRadius: 8,
    padding: '3px 9px',
    cursor: 'pointer',
  },
  tabRow: { display: 'flex', gap: 8 },
  tabBtn: {
    flex: 1,
    padding: '8px 10px',
    borderRadius: 10,
    border: '1.5px solid',
    fontSize: 12,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    background: 'none',
    outline: 'none',
    textAlign: 'center',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  inputWrap: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  textInput: {
    width: '100%',
    padding: '9px 36px 9px 12px',
    borderRadius: 10,
    border: '1.5px solid #e2ddf0',
    fontSize: 14,
    color: '#1a1a2e',
    background: '#fafafa',
    outline: 'none',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
    fontFamily: "'JetBrains Mono', monospace",
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  inputIcon: { position: 'absolute', right: 12, top: 10 },
  errorMsg:  { fontSize: 11, color: '#e05252', margin: 0 },
  inputHint: { fontSize: 11, color: '#aaa', margin: 0 },
  uploadArea: {
    border: '1.5px dashed #d0c8ee',
    borderRadius: 10,
    padding: '12px 14px',
    cursor: 'pointer',
    background: '#faf9fe',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadPrompt: { display: 'flex', alignItems: 'center', gap: 8 },
  uploadText:   { fontSize: 12, color: '#888' },
  uploadExt: {
    fontFamily: "'JetBrains Mono', monospace",
    color: '#7c5cbf',
    fontWeight: 600,
    fontSize: 11,
  },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    width: '100%',
  },
  fileName: {
    fontSize: 12,
    color: '#444',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  clearBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#aaa',
    padding: 2,
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #f0ecfa',
    margin: '2px 0',
  },
  submitBtn: {
    width: '100%',
    padding: '13px 0',
    borderRadius: 12,
    border: 'none',
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: '-0.01em',
    cursor: 'pointer',
    transition: 'opacity 0.2s ease',
    flexShrink: 0,
  },
  submitHint: {
    margin: 0,
    fontSize: 11,
    color: '#a89dcf',
    textAlign: 'center',
    flexShrink: 0,
  },
  errorBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 7,
    background: '#fff5f5',
    border: '1px solid #fecaca',
    borderRadius: 8,
    padding: '8px 10px',
    fontSize: 12,
    color: '#b91c1c',
    flexShrink: 0,
  },
};

// ─── MolstarViewer ────────────────────────────────────────────────────────────
const MolstarViewer = React.forwardRef(function MolstarViewer({ structure }, ref) {
  const iframeRef = useRef(null);
  const loadedRef = useRef(false);
  const queueRef  = useRef([]);

  function postToViewer(msg) {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    try { win.postMessage(msg, MOLSTAR_ORIGIN); } catch { /* noop */ }
  }

  function sendMsg(msg) {
    if (!loadedRef.current) { queueRef.current.push(msg); return; }
    postToViewer(msg);
  }

  React.useImperativeHandle(ref, () => ({ send: sendMsg }));

  useEffect(() => {
    loadedRef.current = false;
    queueRef.current  = [];
    function onMessage(event) {
      if (event.origin !== MOLSTAR_ORIGIN) return;
      if (!iframeRef.current?.contentWindow) return;
      if (event.data?.type !== 'MOLSTAR_READY') return;
      loadedRef.current = true;
      queueRef.current.forEach(postToViewer);
      queueRef.current = [];
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (!structure?.text) return;
    const fmt = structure.format || 'pdb';
    sendMsg({ type: 'LOAD_STRUCTURE', data: structure.text, format: fmt });
    if (fmt === 'pdb' || fmt === 'pdbqt')
      sendMsg({ type: 'LOAD_COMPLEX', structure: structure.text, format: fmt });
  }, [structure]);

  return (
    <iframe
      key={MOLSTAR_URL}
      ref={iframeRef}
      src={MOLSTAR_URL}
      title="Molecular Viewer"
      style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
      allow="fullscreen"
      allowFullScreen
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
    />
  );
});

// ─── TabBtn (local) ───────────────────────────────────────────────────────────
function TabBtn({ active, onClick, label }) {
  return (
    <button
      style={{
        ...st.tabBtn,
        background:  active ? '#ede9f8' : '#fafafa',
        borderColor: active ? '#c4b5e8' : '#e2ddf0',
        color:       active ? '#5a3fa0' : '#666',
        fontWeight:  active ? 600 : 400,
      }}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

// ─── Workflow config ──────────────────────────────────────────────────────────
const WORKFLOW = {
  diffhop: {
    label:      'DiffHop · Scaffold Hopping',
    shortLabel: 'DiffHop',
    color:      '#7c5cbf',
    gradient:   'linear-gradient(135deg, #7c5cbf 0%, #5a3fa0 100%)',
    submitLabel:'Run DiffHop',
    breadcrumb: 'DiffHop Scaffold Hopping',
  },
  difflinker: {
    label:      'DiffLinker · Fragment Linking',
    shortLabel: 'DiffLinker',
    color:      '#0f7ea6',
    gradient:   'linear-gradient(135deg, #0f7ea6 0%, #0a5c7a 100%)',
    submitLabel:'Run DiffLinker',
    breadcrumb: 'DiffLinker Fragment Linking',
  },
};

// ─── Main component ───────────────────────────────────────────────────────────
export default function GenChemUpload({ onSubmitSuccess }) {
  const location = useLocation();
  const navigate  = useNavigate();
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const { userProfile } = useContext(UserContext);

  const { projectName, projectDescription, id: projectId } = location.state || {};
  const userName = activeAccount?.name;
  const userId   = userProfile?.userId;

  // Detect workflow from pathname
  const isDiffLinker = location.pathname.toLowerCase().includes('difflinker');
  const workflow     = isDiffLinker ? WORKFLOW.difflinker : WORKFLOW.diffhop;

  // ── Shared: target structure ────────────────────────────────────────────────
  const [targetTab,      setTargetTab]      = useState('id');
  const [pdbId,          setPdbId]          = useState('');
  const [pdbFile,        setPdbFile]        = useState(null);
  const [rawPdbText,     setRawPdbText]     = useState(null);
  const [activePdbFile,  setActivePdbFile]  = useState(null);
  const [viewerState,    setViewerState]    = useState('empty');
  const [pdbError,       setPdbError]       = useState('');
  const [structure,      setStructure]      = useState(null);
  const [resolvedPdbId,  setResolvedPdbId]  = useState('');

  // ── Shared: chains ──────────────────────────────────────────────────────────
  const [availableChains, setAvailableChains] = useState([]);
  const [selectedChains,  setSelectedChains]  = useState([]);
  const [chainsModified,  setChainsModified]  = useState(false);

  // ── Shared: submission ──────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError,  setSubmitError]  = useState('');
  const [queueResult,  setQueueResult]  = useState(null);
  const [showLoader,   setShowLoader]   = useState(false);

  // ── DiffHop-specific state ──────────────────────────────────────────────────
  const [ligandSource, setLigandSource] = useState('auto');
  const [sdfFile,      setSdfFile]      = useState(null);
  const [nMolecules,   setNMolecules]   = useState(10);

  // ── DiffLinker-specific state ───────────────────────────────────────────────
  const [fragmentSource,   setFragmentSource]   = useState('auto_ligand');
  const [fragmentsFile,    setFragmentsFile]    = useState(null);
  const [anchorMode,       setAnchorMode]       = useState('auto');
  const [anchorIndices,    setAnchorIndices]    = useState('');
  const [linkerSizeMode,   setLinkerSizeMode]   = useState('auto');
  const [linkerSize,       setLinkerSize]       = useState(5);
  const [nSamples,         setNSamples]         = useState(10);

  // ── Refs ────────────────────────────────────────────────────────────────────
  const molstarRef       = useRef(null);
  const debounceRef      = useRef(null);
  const sdfInputRef      = useRef(null);
  const pdbInputRef      = useRef(null);
  const fragmentsInputRef= useRef(null);

  // ─── PDB helpers ─────────────────────────────────────────────────────────────

  function commitPdbText(filtered, id) {
    setStructure({ text: filtered, format: 'pdb' });
    const name = id ? `${id}.pdb` : (pdbFile?.name ?? 'structure.pdb');
    setActivePdbFile(new File([filtered], name, { type: 'chemical/x-pdb' }));
  }

  function applyRawPdb(text, canonicalId = '') {
    setRawPdbText(text);
    const chains = parsePdbChains(text);
    setAvailableChains(chains);
    setSelectedChains(chains);
    setChainsModified(false);
    setResolvedPdbId(canonicalId);
    commitPdbText(text, canonicalId);
  }

  function applyChainFilter(nextChains) {
    if (!rawPdbText) return;
    const noneRemoved = nextChains.length === availableChains.length;
    setChainsModified(!noneRemoved);
    const filtered = noneRemoved
      ? rawPdbText
      : filterPdbChains(rawPdbText, nextChains);
    commitPdbText(filtered, resolvedPdbId);
  }

  function resetTarget() {
    setRawPdbText(null);
    setStructure(null);
    setActivePdbFile(null);
    setAvailableChains([]);
    setSelectedChains([]);
    setChainsModified(false);
    setResolvedPdbId('');
    setViewerState('empty');
    setPdbError('');
  }

  function resetAll() {
    resetTarget();
    setPdbId('');
    setPdbFile(null);
    setTargetTab('id');
    if (pdbInputRef.current) pdbInputRef.current.value = '';
    // diffhop
    setLigandSource('auto');
    setSdfFile(null);
    if (sdfInputRef.current) sdfInputRef.current.value = '';
    setNMolecules(10);
    // difflinker
    setFragmentSource('auto_ligand');
    setFragmentsFile(null);
    if (fragmentsInputRef.current) fragmentsInputRef.current.value = '';
    setAnchorMode('auto');
    setAnchorIndices('');
    setLinkerSizeMode('auto');
    setLinkerSize(5);
    setNSamples(10);
    // submission
    setQueueResult(null);
    setShowLoader(false);
    setSubmitError('');
  }

  // ─── PDB ID debounce ─────────────────────────────────────────────────────────

  const loadPdbById = useCallback(async (id) => {
    if (!id || id.length < 4) { resetTarget(); return; }
    setViewerState('loading');
    setPdbError('');
    try {
      const { pdbId: canonical, pdbText: text } = await resolvePdbId(id);
      applyRawPdb(text, canonical);
      setViewerState('ready');
    } catch (err) {
      setPdbError(err.message);
      setViewerState('error');
      setRawPdbText(null);
      setStructure(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handlePdbIdChange(e) {
    const val = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 4);
    setPdbId(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => loadPdbById(val), 400);
  }

  // ─── PDB file ────────────────────────────────────────────────────────────────

  async function handlePdbFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPdbFile(file);
    setViewerState('loading');
    setPdbError('');
    try {
      const text = await file.text();
      applyRawPdb(text, '');
      setViewerState('ready');
    } catch {
      setPdbError('Could not read PDB file.');
      setViewerState('error');
    }
  }

  async function handleLoadExample() {
    setViewerState('loading');
    setPdbError('');
    try {
      const res = await fetch('/1QCF.pdb');
      if (!res.ok) throw new Error('Example file not found in /public.');
      const text = await res.text();
      setPdbFile(new File([text], '1QCF.pdb', { type: 'chemical/x-pdb' }));
      applyRawPdb(text, '1QCF');
      setViewerState('ready');
    } catch (err) {
      setPdbError(err.message);
      setViewerState('error');
    }
  }

  function handleTargetTabChange(tab) {
    setTargetTab(tab);
    resetTarget();
    if (tab === 'id') { setPdbFile(null); if (pdbInputRef.current) pdbInputRef.current.value = ''; }
    else              { setPdbId(''); }
  }

  function handleChainChange(nextChains) {
    setSelectedChains(nextChains);
    applyChainFilter(nextChains);
  }

  // ─── Anchor highlight via postMessage ────────────────────────────────────────
  // When anchorIndices changes and viewer is ready, ask Molstar to highlight
  // those atom serial numbers. This uses a HIGHLIGHT_ATOMS message — your
  // Molstar viewer must support it; if not, the postMessage is silently ignored.
  useEffect(() => {
    if (!isDiffLinker) return;
    if (viewerState !== 'ready') return;
    if (anchorMode !== 'manual' || !anchorIndices.trim()) return;
    const indices = anchorIndices
      .split(',')
      .map((p) => parseInt(p.trim(), 10))
      .filter((n) => !isNaN(n) && n > 0);
    if (indices.length < 2) return;
    molstarRef.current?.send({
      type:        'HIGHLIGHT_ATOMS',
      atomSerials: indices,
      color:       '#f59e0b', // amber — visible against purple protein cartoon
    });
  }, [anchorIndices, anchorMode, viewerState, isDiffLinker]);

  // ─── Submit guard ─────────────────────────────────────────────────────────────

  const diffhopReady =
    !isDiffLinker &&
    (ligandSource === 'auto' || (ligandSource === 'upload' && sdfFile));

  const anchorIndicesValid =
    anchorMode === 'auto' ||
    (anchorIndices.trim() &&
      anchorIndices.split(',').every((p) => /^\d+$/.test(p.trim()) && parseInt(p.trim(), 10) >= 1) &&
      anchorIndices.split(',').length >= 2);

  const difflinkerReady =
    isDiffLinker &&
    (fragmentSource === 'auto_ligand' || (fragmentSource === 'upload' && fragmentsFile)) &&
    anchorIndicesValid &&
    (linkerSizeMode === 'auto' || (linkerSize >= 3 && linkerSize <= 10)) &&
    nSamples >= 10 && nSamples <= 500;

  const canSubmit =
    viewerState === 'ready' &&
    !isSubmitting &&
    (isDiffLinker ? difflinkerReady : diffhopReady);

  const submitHint = (() => {
    if (viewerState !== 'ready') return 'Load a target structure to continue';
    if (isDiffLinker) {
      if (fragmentSource === 'upload' && !fragmentsFile) return 'Upload a fragments file to continue';
      if (!anchorIndicesValid) return 'Enter valid anchor atom indices to continue';
    } else {
      if (ligandSource === 'upload' && !sdfFile) return 'Upload a scaffold SDF to continue';
    }
    return '';
  })();

  // ─── Submit ───────────────────────────────────────────────────────────────────

  async function handleSubmit() {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setShowLoader(true);
    setSubmitError('');
    try {
      let result;
      if (isDiffLinker) {
        result = await generateLinker({
          fragmentSource,
          pdbId:         targetTab === 'id' ? resolvedPdbId || pdbId : undefined,
          anchorMode,
          anchorIndices: anchorMode === 'manual' ? anchorIndices : undefined,
          linkerSizeMode,
          linkerSize:    linkerSizeMode === 'manual' ? linkerSize : undefined,
          nSamples,
          userId, userName, projectId, projectName,
          pdbFile:        targetTab === 'file' ? activePdbFile : undefined,
          fragmentsFile:  fragmentSource === 'upload' ? fragmentsFile : undefined,
        });
      } else {
        result = await generateScaffolds({
          ligandSource:  ligandSource === 'auto' ? 'auto_ligand' : 'upload_ligand',
          pdbId:         targetTab === 'id' ? resolvedPdbId || pdbId : undefined,
          nMolecules,
          userId, userName, projectId, projectName,
          proteinFile:   targetTab === 'file' ? activePdbFile : undefined,
          ligandFile:    ligandSource === 'upload' ? sdfFile : undefined,
        });
      }
      setQueueResult(result);
      onSubmitSuccess?.(result.taskId);
    } catch (err) {
      setShowLoader(false);
      setSubmitError(err.message || 'Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Breadcrumb */}
      <div style={st.breadcrumb}>
        <Link to="/dashboard" className="hover:text-[#1e1b2e] hover:underline">Home</Link>
        <span style={{ margin: '0 2px' }}>&rsaquo;</span>
        <Link to="/generativeai" className="hover:text-[#1e1b2e] hover:underline">Projects</Link>
        <span style={{ margin: '0 2px' }}>&rsaquo;</span>
        <button type="button" onClick={() => navigate(-2)} className="hover:text-[#1e1b2e] hover:underline">
          Project Details
        </button>
        <span style={{ margin: '0 2px' }}>&rsaquo;</span>
        <button type="button" onClick={() => navigate(-1)} className="hover:text-[#1e1b2e] hover:underline">
          Flow Selection
        </button>
        <span style={{ margin: '0 2px' }}>&rsaquo;</span>
        <b style={{ color: '#1e1b2e', fontWeight: 700 }}>{workflow.breadcrumb}</b>
      </div>

      <div style={st.root}>

        {/* ── LEFT: Molstar viewer ────────────────────────────────────── */}
        <div style={st.previewCard}>
          <div style={st.previewHeader}>
            <span style={st.previewTitle}>Structure preview</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  ...st.workflowPill,
                  background: workflow.gradient,
                }}
              >
                {workflow.shortLabel}
              </span>
              <span style={st.molstarBadge}>Molstar</span>
            </div>
          </div>

          <div style={st.viewerWrap}>
            {viewerState === 'empty' && (
              <div style={st.emptyState}>
                <Dna size={40} color="#c4b5e8" strokeWidth={1.3} />
                <p style={st.emptyText}>
                  Enter a PDB ID or upload a file to preview the target structure.
                  <br />
                  {isDiffLinker
                    ? 'Anchor atoms will be highlighted in amber once set.'
                    : 'The binding pocket will be auto-detected from the structure.'
                  }
                </p>
              </div>
            )}

            {viewerState === 'loading' && (
              <div style={st.centeredState}>
                <Loader2 size={28} color="#9d84d8" strokeWidth={1.8} style={st.spinner} />
                <p style={st.stateText}>Fetching structure…</p>
              </div>
            )}

            {viewerState === 'error' && (
              <div style={st.centeredState}>
                <AlertCircle size={30} color="#e05252" strokeWidth={1.6} />
                <p style={st.errorText}>{pdbError || 'Could not load structure'}</p>
              </div>
            )}

            <div
              style={{
                ...st.iframeLayer,
                opacity:       viewerState === 'ready' ? 1 : 0,
                pointerEvents: viewerState === 'ready' ? 'auto' : 'none',
              }}
            >
              <MolstarViewer ref={molstarRef} structure={structure} />
            </div>
          </div>

          {/* Info banners below viewer */}
          {viewerState === 'ready' && !isDiffLinker && ligandSource === 'auto' && (
            <div style={{ ...st.banner, background: '#f5f0ff', border: '1px solid #d9cff5', color: '#5a3fa0' }}>
              <CheckCircle2 size={13} color="#7c5cbf" strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                Pocket will be auto-detected. The co-crystallised heteroatom ligand
                is shown as the inferred pocket reference — not your output scaffold.
              </span>
            </div>
          )}

          {viewerState === 'ready' && isDiffLinker && anchorMode === 'manual' && anchorIndices && (
            <div style={{ ...st.banner, background: '#fffbeb', border: '1px solid #f5d87a', color: '#7a5a00' }}>
              <AlertCircle size={13} color="#b07d00" strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                Anchor atoms <strong>{anchorIndices}</strong> are highlighted in amber in
                the viewer. Verify they correspond to the intended attachment points on
                each fragment before submitting.
              </span>
            </div>
          )}

          {viewerState === 'ready' && chainsModified && (
            <div style={{ ...st.banner, background: '#fffbeb', border: '1px solid #f5d87a', color: '#7a5a00' }}>
              <AlertCircle size={13} color="#b07d00" strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                Viewer and API will use the <strong>filtered structure</strong> — chains{' '}
                <strong>{selectedChains.join(', ')}</strong> only.
              </span>
            </div>
          )}
        </div>

        {/* ── RIGHT: Controls ──────────────────────────────────────────── */}
        <div style={st.controlsCol}>

          {/* Target structure card — shared by both workflows */}
          <div style={st.card}>
            <div style={st.cardHeaderRow}>
              <p style={st.cardLabel}>Target structure</p>
              {targetTab === 'file' && (
                <button style={st.exampleBtn} onClick={handleLoadExample}>
                  Get example
                </button>
              )}
            </div>

            <div style={st.tabRow}>
              <TabBtn active={targetTab === 'id'}   onClick={() => handleTargetTabChange('id')}   label="PDB ID" />
              <TabBtn active={targetTab === 'file'} onClick={() => handleTargetTabChange('file')} label="Upload PDB" />
            </div>

            {targetTab === 'id' && (
              <div style={st.inputWrap}>
                <input
                  style={{
                    ...st.textInput,
                    borderColor:
                      viewerState === 'error' ? '#e05252'
                      : viewerState === 'ready' ? '#b9a9e4'
                      : '#e2ddf0',
                  }}
                  type="text"
                  placeholder="e.g. 4HJO"
                  value={pdbId}
                  onChange={handlePdbIdChange}
                  maxLength={4}
                  spellCheck={false}
                  autoComplete="off"
                />
                {viewerState === 'loading' && (
                  <Loader2 size={15} color="#9d84d8" style={{ ...st.inputIcon, ...st.spinner }} />
                )}
                {viewerState === 'ready' && pdbId && (
                  <CheckCircle2 size={15} color="#7c5cbf" style={st.inputIcon} />
                )}
                {viewerState === 'error' && (
                  <AlertCircle size={15} color="#e05252" style={st.inputIcon} />
                )}
                {viewerState === 'error'  && <p style={st.errorMsg}>{pdbError}</p>}
                {viewerState === 'ready' && resolvedPdbId && (
                  <p style={st.inputHint}>
                    Resolved: <strong>{resolvedPdbId}</strong> — loaded from RCSB
                  </p>
                )}
              </div>
            )}

            {targetTab === 'file' && (
              <div style={st.uploadArea} onClick={() => pdbInputRef.current?.click()}>
                <input
                  ref={pdbInputRef}
                  type="file"
                  accept=".pdb,.pdbqt,.ent"
                  style={{ display: 'none' }}
                  onChange={handlePdbFileChange}
                />
                {pdbFile ? (
                  <div style={st.fileRow}>
                    <CheckCircle2 size={14} color="#7c5cbf" />
                    <span style={st.fileName}>{pdbFile.name}</span>
                    <button
                      style={st.clearBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPdbFile(null);
                        resetTarget();
                        if (pdbInputRef.current) pdbInputRef.current.value = '';
                      }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <div style={st.uploadPrompt}>
                    <Upload size={15} color="#9d84d8" strokeWidth={1.6} />
                    <span style={st.uploadText}>
                      Click to upload <span style={st.uploadExt}>.pdb / .pdbqt</span>
                    </span>
                  </div>
                )}
              </div>
            )}

            {viewerState === 'ready' && availableChains.length > 0 && (
              <>
                <hr style={st.divider} />
                <ChainSelector
                  availableChains={availableChains}
                  selectedChains={selectedChains}
                  onChange={handleChainChange}
                  disabled={viewerState === 'loading'}
                />
              </>
            )}
          </div>

          {/* Workflow-specific inputs */}
          {isDiffLinker ? (
            <DiffLinkerInputs
              fragmentSource={fragmentSource}
              onFragmentSource={setFragmentSource}
              fragmentsFile={fragmentsFile}
              onFragmentsFile={setFragmentsFile}
              anchorMode={anchorMode}
              onAnchorMode={setAnchorMode}
              anchorIndices={anchorIndices}
              onAnchorIndices={setAnchorIndices}
              linkerSizeMode={linkerSizeMode}
              onLinkerSizeMode={setLinkerSizeMode}
              linkerSize={linkerSize}
              onLinkerSize={setLinkerSize}
              nSamples={nSamples}
              onNSamples={setNSamples}
              fragmentsInputRef={fragmentsInputRef}
            />
          ) : (
            <DiffHopInputs
              ligandSource={ligandSource}
              onLigandSource={setLigandSource}
              sdfFile={sdfFile}
              onSdfFile={setSdfFile}
              nMolecules={nMolecules}
              onNMolecules={setNMolecules}
              sdfInputRef={sdfInputRef}
            />
          )}

          {/* Submit error */}
          {submitError && (
            <div style={st.errorBanner}>
              <AlertCircle size={13} color="#b91c1c" strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{submitError}</span>
            </div>
          )}

          <div style={{ flex: 1 }} />

          <button
            style={{
              ...st.submitBtn,
              background: workflow.gradient,
              boxShadow:  `0 2px 14px ${workflow.color}44`,
              opacity:    canSubmit ? 1 : 0.45,
              cursor:     canSubmit ? 'pointer' : 'not-allowed',
            }}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {workflow.submitLabel}
          </button>

          {submitHint && (
            <p style={st.submitHint}>{submitHint}</p>
          )}
        </div>
      </div>

      {/* Full-screen loader */}
      {showLoader && (
        <div className="fixed inset-0 flex items-center justify-center backdrop-blur-sm bg-black/20 z-50">
          <Loader />
        </div>
      )}

      {/* Queue modal */}
      {queueResult && (
        <QueueNotificationModal
          taskId={queueResult.taskId}
          projectId={projectId}
          workflowType="generativeai"
          onClose={resetAll}
        //   onHistory={resetAll}
        />
      )}
    </>
  );
}